// Translation Service for Multi-language Support
// =============================================

import enTranslations from '../locales/en.json';
import thTranslations from '../locales/th.json';

export type Language = 'en' | 'th';

// Define the structure based on the JSON files
export interface TranslationKeys {
  home: {
    title: string;
    subtitle: string;
    poweredBy: string;
    buttonStart?: string;
  };
  common?: {
    loading: string;
    error: string;
    retry: string;
    next: string;
    previous: string;
    submit: string;
    cancel: string;
    confirm: string;
    close: string;
  };
  navigation?: {
    home: string;
    quiz: string;
    results: string;
    settings: string;
  };
  quiz?: {
    start: string;
    question: string;
    progress: string;
    complete: string;
    score: string;
  };
  results?: {
    title: string;
    score: string;
    description: string;
    share: string;
    retake: string;
  };
  settings?: {
    title: string;
    language: string;
    theme: string;
    notifications: string;
  };
}

// Helper function to get nested value from object using dot notation
const getNestedValue = (obj: any, path: string): string => {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
};

// Translation service class
class TranslationService {
  private currentLanguage: Language = 'en';
  private translations: Record<Language, TranslationKeys> = {
    en: enTranslations,
    th: thTranslations,
  };

  // Get current language
  getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  // Set current language
  setLanguage(language: Language): void {
    this.currentLanguage = language;
    // Store in localStorage for persistence
    localStorage.setItem('preferred-language', language);
  }

  // Initialize language from localStorage or browser preference
  initializeLanguage(): void {
    const stored = localStorage.getItem('preferred-language') as Language;
    if (stored && (stored === 'en' || stored === 'th')) {
      this.currentLanguage = stored;
    } else {
      // Detect browser language
      const browserLang = navigator.language.split('-')[0];
      this.currentLanguage = browserLang === 'th' ? 'th' : 'en';
    }
  }

  // Get translation for a key
  t(key: string): string {
    return getNestedValue(this.translations[this.currentLanguage], key);
  }

  // Get all translations for current language
  getAllTranslations(): TranslationKeys {
    return this.translations[this.currentLanguage];
  }

  // Check if a translation key exists
  hasTranslation(key: string): boolean {
    return getNestedValue(this.translations[this.currentLanguage], key) !== key;
  }

  // Get available languages
  getAvailableLanguages(): { code: Language; name: string; nativeName: string }[] {
    return [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'th', name: 'Thai', nativeName: 'ไทย' },
    ];
  }
}

// Create singleton instance
const translationService = new TranslationService();

// Initialize with stored preference or browser language
translationService.initializeLanguage();

export default translationService;
